import { useCallback, useMemo, useState } from "react";

function matchesSearch(text: string, query: string): boolean {
  if (!query.trim()) return true;
  return text.toLowerCase().includes(query.toLowerCase().trim());
}

interface UseTaxonomySearchResult<T> {
  query: string;
  setQuery: (q: string) => void;
  filtered: T[];
}

export function useCategorySearch<T extends { title: string }>(
  items: T[],
): UseTaxonomySearchResult<T> {
  const [query, setQuery] = useState("");
  const filtered = useMemo(
    () => items.filter((item) => matchesSearch(item.title, query)),
    [items, query],
  );
  return { query, setQuery, filtered };
}

interface UseTagGroupSearchResult {
  getQuery: (groupId: string) => string;
  setQuery: (groupId: string, q: string) => void;
  getFiltered: <T extends { label: string }>(
    groupId: string,
    options: T[],
  ) => T[];
}

export function useTagGroupSearch(): UseTagGroupSearchResult {
  const [queries, setQueries] = useState<Record<string, string>>({});

  const getQuery = useCallback(
    (groupId: string) => queries[groupId] || "",
    [queries],
  );

  const setQuery = useCallback(
    (groupId: string, q: string) =>
      setQueries((prev) => ({ ...prev, [groupId]: q })),
    [],
  );

  const getFiltered = useCallback(
    <T extends { label: string }>(groupId: string, options: T[]): T[] => {
      const q = queries[groupId] || "";
      if (!q.trim()) return options;
      return options.filter((opt) => matchesSearch(opt.label, q));
    },
    [queries],
  );

  return { getQuery, setQuery, getFiltered };
}
